/**
 * This is an extension of the default kendo grid.
 *
 * It currently adds support for a reoderableRows property.  This defaults to false.  When set to a column field name,
 * it will order the grid by that field, and allow the user to re-order the grid rows while updating that field on all rows.
 * Fires an event "reorder" whenever the rows are re-ordered
 *
 * Adds support for a column command: "checkbox".  This column command display a column with a checkbox for each row.
 * The checkChange event is fired whenever the status of at least one of these checkboxes changes.  The checked() method on the grid
 * returns all checked rows(the actual TR elements).  The userCheckChange event is fired whenever a user clicks a checkbox to
 * change its value, including checking a group or all (through the header).
 *
 * Supports an extra localizedTitle property on each column.  This takes a key that will be passed to the locale manager, and the
 * result will be set to the title property.
 */
import DFGridCheckbox from "app/widgets/DFGrid/DFGridCheckbox";
import DFGridKeyboard from "app/widgets/DFGrid/DFGridKeyboard";
import DFGridAutoSaveColumns from "app/widgets/DFGrid/DFGridAutoSaveColumns";
import DFGridResizableColumnOption from "app/widgets/DFGrid/DFGridResizableColumnOption";
import DFGridRowsReorderable from "app/widgets/DFGrid/DFGridRowsReorderable";
import DFPageable from "app/widgets/DFGrid/DFGridPageable";
import DFGridEdit from "app/widgets/DFGrid/DFGridEdit";
import DFGridFiltering from "app/widgets/DFGrid/DFGridFiltering";
import DFGridSorting from "app/widgets/DFGrid/DFGridSorting";
import DFGridGrouped from "app/widgets/DFGrid/DFGridGrouped";
import DFGridDetailRow from "app/widgets/DFGrid/DFGridDetailRow";
import SavedSelection from "app/widgets/DFGrid/SavedSelection";
import SavedChecks from "app/widgets/DFGrid/SavedCheckedState";
import DFGridScroll from "app/widgets/DFGrid/DFGridScroll";
import DFGridWordWrap from "app/widgets/DFGrid/DFGridWordWrap";
import SavedKeyboardFocus from "app/widgets/DFGrid/SavedKeyboardFocus";
import SavedDetailRow from "app/widgets/DFGrid/SavedDetailRow";
import DFGridAria from "app/widgets/DFGrid/DFGridAria";
import DFGridMobile from "app/widgets/DFGrid/DFGridMobile";
import BaseWidget from "common/widgets/base/BaseWidget";
import locale from "common/managers/locale";
import functions from "common/helper/functions";
import domUtils from "common/helper/domUtils";
import "common/bindings/field";
import DFGridAdd from "app/widgets/DFGrid/DFGridAdd";
import DFGridLoading from "app/widgets/DFGrid/DFGridLoading";
import PlainTextCopy from "common/helper/PlainTextCopy";

const DFGrid = BaseWidget(kendo.ui.Grid)
    .extend(DFGridCheckbox)
    .extend(DFGridKeyboard)
    .extend(DFGridAutoSaveColumns)
    .extend(DFGridResizableColumnOption)
    .extend(DFGridRowsReorderable)
    .extend(DFPageable)
    .extend(DFGridEdit)
    .extend(DFGridFiltering)
    .extend(DFGridSorting)
    .extend(DFGridGrouped)
    .extend(DFGridDetailRow)
    .extend(DFGridScroll)
    .extend(DFGridWordWrap)
    .extend(DFGridAria)
    .extend(DFGridMobile)
    .extend(DFGridAdd)
    .extend(DFGridLoading)
    .extend({
        options: {

            name: "DFGrid",

            /**
             * Used in Admin.
             * Add in a pager to control the grid.  Like the pageable option kendo supports by default.
             * However, this pager is the DFPager that is already configured correctly.
             * Set to "after" to insert the pager after the grid instead of before
             */
            pager: false,

            /**
             * Used in Main, when the pageSizes dropdown is not needed.
             * Correctly configured the pageable option of the kendo Grid, using a kendo.ui.Pager.
             * Styled for most grids in Main.
             */
            defaultPaging: false,

            /**
             * Whether to always show pager (when applicable).
             */
            alwaysShowPager: false,

            // Whether to show the result count for a single page of results
            showPagerTotal: false,

            /**
             * The locale entry key for the paging message
             * This is used as the message when defaultPaging is true
             */
            pagerDisplayKey: "pager.total",

            /**
             * When set to a column field, that column is used to order the grid, users can also
             * drag and drop rows to re-order them.
             */
            reorderableRows: false,

            /**
             * When a row is reorderd, should a set() be used on the model so that dirty/unsavedChanges are automatically udpated.
             */
            changeOnReorder: false,

            /**
             * The index of the first item in list of options
             */
            reorderableRowsBase: 0,

            /**
             * When set to true, if a single row is checked it will be selected.
             */
            selectOnCheck: false,

            /**
             * When set to true, when a single row is clicked, it will be checked. And ALL OTHER ROWS WILL BE UNCHECKED.
             *
             * Selectable must be FALSE.  NOT COMPATIBLE WITH SELECTABLE AT THIS TIME.
             * (Can be extended to handle it, but not needed per design at this time, so not adding support.)
             *
             * checkboxField must be defined.
             */
            checkOnClick: false,

            /**
             * When set to true, when a single is clicked, its checkbox value will be rotated through all valid values.
             *
             * Selectable must be FALSE. NOT COMPATIBLE WITH SELECTABLE.
             */
            rotateCheckOnClick: false,

            /**
             * When set to true, when a single row is right clicked, it will be checked.
             *
             * This is generally implied by checkOnClick if selectable is false.
             */
            checkOnRightClick: false,

            /**
             * When set to a field, that field will be updated to reflect the current state of the checkbox
             */
            checkboxField: false,

            /**
             * When set to true, checkboxField must be defined.  The state of checkboxes will be stored across pages.
             */
            trackChecked: false,

            /**
             * When trackChecked is true, items must have a unique field that can identify an item.
             * It can be defined with this option.  It will default to the id field of the model of the
             * datasource in the grid.
             */
            uniqueField: false,

            /**
             * The value of the checkbox field that causes the row to be counted as selected.
             */
            selectionViewValue: null,

            /**
             * When false, the selection view will be added to the grid.
             */
            hideSelectionView: false,

            /**
             * By default, the checkboxes will be checked when checkboxField on the item is true.  To negate the
             * checkboxField, set this to true.
             * 
             * This is a shortcut for specifying the exact values for checked/unchecked.
             */
            negateCheckboxField: false,

            //The value for the checked checkbox
            checkedCheckboxFieldValue: true,

            //The value for the unchecked checkbox
            uncheckedCheckboxFieldValue: false,

            //The value for the partially checked checkbox
            partialCheckboxFieldValue: "PARTIALLY_CHECKED_VALUE",

            /**
             * This must be true AND there must be a hidden column with a groupHeaderTemplate, then checkboxes will
             * be added to the categories of the groups.
             */
            groupedCheckboxes: false,

            /**
             * When set to a field, that field will control whether the checkbox for a row is disabled.
             */
            disableCheckboxField: false,

            /**
             * If a class should be added to the filter window, this is the name of the class to add.
             * Available filter classes are currently:
             * - simpleFilter
             * - simpleRowFilter
             * - simpleDateFilter
             * but more may be added to designs as we progress.
             */
            filterClass: "simpleFilter",

            /**
             * The operator to use for menu filtering, which applies when filterClass == 'simpleFilter' || 'simpleRowFilter'.
             * Current valid options are "contains", "startswith", and "endswith".
             */
            filterOperator: "contains",

            /**
             * When true, navigating to a new row with the keyboard will cause a selection to occur
             */
            selectOnFocus: false,

            /**
             * Causes the grid to store the selection on refresh of the grid when set to true.
             * Default behavior will select the first row if all previously selected rows are missing.
             * Also supports being set to "allowEmpty" to allow nothing to be selected when all previously selected rows are missing.
             */
            keepSelectionOnRefresh: false,

            /**
             * Keep checks that are stored client-side only on refresh of the grid. Used when checkboxField is a transient
             * field - NOT when data is pulled back from the server to populate the checks.
             */
            keepChecksOnRefresh: false,

            /**
             * If set, when a row's dataItem has this property, and it is false, the row will be marked as invalid.
             */
            validationField: "",

            /**
             * If set to true, kendo.bind will be called on all widgets in column templates.  They will be bound to the
             * observable for that row.
             */
            bindWidgets: false,

            /**
             * If set to true, kendo.bind will be called on all widgets in the detail row template.  They will be bound to the
             * observable for that row.
             *
             * Adds a detailFirstExpand event that is fired the first time a detail row is expanded.  This event will pass
             * a masterRowItem field and a bindTo field.
             *
             * If set to string "new", the row will be bound to a new observable.
             */
            bindWidgetsInDetailRow: false,

            /**
             * When using detail rows, whether clicking a row will automatically expand or collapse it.
             * Defaults to true.
             */
            autoExpandDetailRow: true,

            /**
             * The field of the dataItem that holds the main content of the detail row, i.e. "description".
             * When set, and there is a detailTemplate, if empty, the arrow to expand the detailRow will be hidden.
             * Works only if the field comes back in the initial call to get the data. If a server call is made
             * to populate the detailRow data on expand, this field can not be used.
             */
            detailRowField: false,


            /**
             * Whether the enter key on a table row should act like a double click event.
             */
            doubleClickOnEnterKey: false,

            /**
             * Select a row whenever the context menu is used on that row.
             */
            selectOnContext: false,

            /**
             * Field to use for value().
             */
            dataValueField: "id",
            valuePrimitive: true,

            /**
             * WindowSetting.windowGroup name to save column settings.  Default resizable and reorderable options
             * control which items will trigger saving.
             */
            autoSaveColumns: false,

            // boolean fluid will add a spacer as the last column for layout spacing
            fluid: false,

            /**
             * Whether word wrap is enabled.
             */
            wordWrap: false,

            /** Whether count of total results is imprecise. */
            impreciseCount: false,

            /** When true, autoScroll accounts for selection and checkboxes. When false, it moves grid to top. */
            autoScrollToSelection: true,

            /** When true, scrolltop is set after refresh based on position before refresh. */
            scrollToPositionOnRefresh: false,

            /** When true, keyboard focus is maintained during refresh. */
            focusOnRefresh: false,

            /** When true, detail row state should remain the same after a grid refresh. */
            restoreDetailRowOnRefresh: false,

            /** When false, arrow controls for collapsing groups will be hidden. */
            groupsCollapsible: true,

            /** When true, formatting will be stripped on copy. */
            plainTextCopy: false,

            //Configure the message when no records are available to display.
            //Needs role='alert' so that it will be read by screenreaders on filtering.
            noRecords: {
                template: function() {
                    return "<div class='k-grid-norecords-template' role='alert'>" + locale.get("filter.noData") + "</div>";
                }
            },

            // When set to an object like {add: 'locale.key', addFirst: 'locale.key'} it will cause the grid to create add buttons that appear when necessary to allow the grid to be added to.
            addButtons: false,

            readonly: false,

            // when true, will use "message.options.none" as the locale key; when not false, a message will be shown when there is no data returned in the grid
            noRecordsKey: false,

            // needs to be set if there are filters on the DS that can not be changed by the user, to account for during display of noRecordsKey
            filtersCount: 0,

            // expose template settings to the view so templates can turn off "with" block (so column names can be "data")
            templateSettings: null,

            size: "small"
        },

        events: [
            "considerRefresh"
        ].concat(kendo.ui.Grid.fn.events),

        init: function(element, options, events) {
            if ($(element).attr("id") == undefined) {
                console.error("DFGrid widgets require an id");
            }

            this._configCheckBoxCommand(options);
            this._configRowsReorderable(options.reorderableRows, options);

            if (options.validationField) {
                this.validationField = options.validationField;
            }

            this._configFiltering(options);

            // Set specific column options, such as editing command column,
            // look up localized titles in locale file, and set filtering ui
            // - all if set by options
            if (options.columns) {
                for (var i = 0; i < options.columns.length; i++) {
                    if (options.columns[i].localizedTitle) {
                        options.columns[i].title = locale.get(options.columns[i].localizedTitle);
                    }
                    if (options.columns[i].values) {
                        for (var j = 0; j < options.columns[i].values.length; j++) {
                            if (options.columns[i].values[j].localized) {
                                options.columns[i].values[j].text = locale.get(options.columns[i].values[j].localized)
                            }
                        }
                    }

                    this._configEditingColumn(options, options.columns[i]);

                    this._configFilteringColumn(options, options.columns[i]);
                }
            }

            this._configEditing(options, $(element));
            this._configGrouping(options, $(element));

            this._configDetailRow(options);

            this._fluidColumn(options);

            this._configMobileColumns(options);

            kendo.ui.Grid.fn.init.call(this, element, options, events);

            if (options.wordWrap) {
                this.initWordWrap();
            }

            this._prepareRowsReorderable();
            this._prepareCheckBox();
            this._prepareFiltering();
            this._prepareDetailRow();
            this._prepareLoadingBar();

            this._prepareSorting();

            this._prepareEditing();

            // If using a percentage based height, the grid's height needs to be
            // recalculated when the window is resized.
            if (options.height && options.height.toString().indexOf("%") != -1) {
                this._windowResize = functions.debounce(this.resize, 50, this);
                $(window).on("resize", this._windowResize);
            }

            this._prepareDisableHook();

            this._prepareKeyboardHooks();

            this._prepareSelectScroll();

            this._prepareSelectContext();

            this._prepareDoubleClick();

            this._autoSaveColumns();

            this._prepareGroupsCollapsible();

            this._preparePlainTextCopy();
            this._prepareColumnMenu();
        },

        _dataSource() {
          kendo.ui.Grid.fn._dataSource.apply(this, arguments);
          this._bindLoadingHandlers(this.dataSource);
        },

        /**
         * Handle the context menu.
         * @private
         */
        _prepareSelectContext: function() {
            var me = this;
            if (me.options.selectOnContext) {
                me.element.on("contextmenu", "tr[data-uid]", function(e) {
                    var found = false;
                    var current = me.select();
                    var target = e.currentTarget;
                    for (var i = 0; i < current.length && !found; i++) {
                        if (current[i] == target) {
                            found = true;
                        }
                    }
                    if (!found) {
                        me.clearSelection();
                        me.select(e.currentTarget);
                    }
                });
            }
        },

        /**
         * Setup doubleClick event.
         * @private
         */
        _prepareDoubleClick: function() {
            var me = this;
            this.element.off("dblclick.DFGrid").on("dblclick.DFGrid", "tr[data-uid]", function(e) {
                if (me.enabled && !$(e.target).is("input, a")) {
                    me.doubleClick(e.currentTarget);
                }
            });
        },

        _prepareGroupsCollapsible: function() {
            if (!this.options.groupsCollapsible) {
                this.bind("dataBound", function() {
                    $($(this.element).find("a.k-icon.k-svg-i-caret-alt-down")).each(function(idx, el) {
                        $(el).remove();
                    });
                });
            }
        },

        _preparePlainTextCopy() {
            if(this.options.plainTextCopy) {
                this._plainTextCopy = new PlainTextCopy(this.element[0]);
            }
        },

        _cleanupPlainTextCopy() {
            this._plainTextCopy?.destroy();
            delete this._plainTextCopy;
        },

        _prepareColumnMenu() {
            if(this.options.columnMenu) {
                this.wrapper.addClass("columnMenuGrid");
            }
            this.bind("columnMenuInit", (e) => {
                e.container.addClass("DFGridColumnMenu");
            });
        },

        /**
         * Double click a row.
         * @param row
         */
        doubleClick: function(row) {
            var item = this.dataItem(row);
            var selected = $(row).is(".k-state-selected");
            this.trigger("doubleClick", {
                row: row,
                item: item,
                isSelected: selected,
                index: this.element.find("tr[data-uid]").index(row)
            });
        },

        _makeWidgetsActive: function() {
            if (this.options.bindWidgets) {
                var me = this;
                this.tbody.find("tr").each(function(i, el) {
                    el = $(el);
                    kendo.bind(el, me.dataSource.getByUid(el.attr("data-uid")));
                    me.trigger("widgetsBoundForRow", {
                        row: el
                    });
                    me.one("dataBinding", function() {
                        kendo.destroy(el);
                    })
                })
            }
        },

        _setInvalidClass: function() {
            var me = this;
            if (me.validationField != "" && me.validationField != undefined) {
                $.each(this.tbody.find("tr[data-uid]"), function(index, row) {
                    var rowItem = me.dataItem(row);
                    if (!rowItem[me.validationField]) {
                        $(row).find("td").eq(0).addClass("invalid");
                    }
                });
            }
        },

        /**
         * Determines whether one and only one row is selected in the grid.
         * Used for bindings.
         */
        hasSelectedRow: function() {
            return this.select().length == 1;
        },

        refresh: function(e) {
            if ((e && e.action === 'itemchange' && this.editable) ||
                this._haltRefresh == true || this.trigger("considerRefresh", {
                    originalEvent: e
                })) {
                this.trigger("stoppedRefresh", e);
                this.progress(false);
                return;
            } else {
                this.trigger("allowedRefresh", e);
            }

            var selection;
            if (this.options.keepSelectionOnRefresh && this.options.selectable) {
                selection = new SavedSelection(this);
                selection.save();
            }
            var checkedState;
            if (this.options.keepChecksOnRefresh) {
                checkedState = new SavedChecks(this);
                checkedState.save();
            }

            var detailRowState;
            if (this.options.restoreDetailRowOnRefresh) {
                detailRowState = new SavedDetailRow(this);
            }

            var scrollTop;
            if (this.options.scrollToPositionOnRefresh) {
                scrollTop = this.content.scrollTop();
            }

            var saveFocus;
            if (this.options.focusOnRefresh) {
                saveFocus = new SavedKeyboardFocus(this);
            }

            this._preRefreshCheckboxes();
            this._preRefreshReorderable();

            // https://www.telerik.com/account/support-center/view-ticket/1645978
            e && (e.partialUpdate = false);
            kendo.ui.Grid.fn.refresh.apply(this, arguments);
            if (selection && !this.trigger("considerRestoreSelection", {
                    originalEvent: e
                })) {
                selection.restore();
            }
            this._makeCheckBoxCommand();
            if (checkedState && !this.trigger("considerRestoreSelection", {
                    originalEvent: e
                })) {
                checkedState.restore();
            }

            this._updatePager();
            this._makeRowsReorderable();
            this._makeSortedColumnsBold();
            this._setInvalidClass();
            this._makeWidgetsActive();
            this._configEditableCommand();
            this._restrictResizable();
            this._makeGridARIACompliant();
            this._updateAddButton();

            if (this.options.restoreDetailRowOnRefresh) {
                detailRowState.restore(this);
            }

            this.autoScroll();

            if (this.options.scrollToPositionOnRefresh) {
                this.content.scrollTop(scrollTop);
            }

            if (this.options.focusOnRefresh) {
                saveFocus.restore(this);
            }

            this.wrapper.removeClass("keepHeader");
            this._refreshEnabled();
        },

        /**
         * Scroll to the first selected or checked item, or to the top if none.
         */
        autoScroll: function() {
            if (this.options.scrollable && this.options.autoScrollToSelection) {

                // find first selected
                var row = undefined;
                if (this.options.selectable) {
                    var selected = this.select();
                    if (selected.length) {
                        row = selected.first();
                    }
                }

                // find first checked row only if none selected
                if (!row) {
                    row = this.firstChecked();
                }

                // scroll to row, otherwise to top
                if (row) {
                    domUtils.scrollIntoView(row);
                } else {
                    domUtils.scrollToTop(this.content);
                }
            } else if (this.options.scrollable) {
                domUtils.scrollToTop(this.content);
            }
        },

        /**
         * Pause the refresh of the grid
         * @param pause
         * @param skipRefresh Can the automation refresh when pause = false be skipped?
         */
        pauseRefresh: function(pause, skipRefresh) {
            if (this._haltRefresh != pause) {
                this._haltRefresh = pause;
                if (!this._haltRefresh) {
                    skipRefresh || this.refresh();
                }
            }
        },

        onHide: function() {
            this.hideFilterPopups();
        },

        progress: function(toggle) {
            //Wrap internal kendo api.  Can also be implemented using kendo.ui.progress
            this._progress(toggle);
        },

        ready: function() {
            var def = $.Deferred();
            this.one("dataBound", function() {
                def.resolve();
            });
            return def.promise();
        },

        destroy: function() {
            this._cleanupKeyboardHook();
            this._cleanupLoadingBar();
            this._cleanupPlainTextCopy();
            if (this.element) {
                const el = this.element;
                if (this.options.wordWrap) {
                    this.destroyWordWrap();
                }
                if (this._windowResize) {
                    $(window).off("resize", this._windowResize);
                    delete this._windowResize;
                }
                // order matters, super has to be called before removeData for proper cleanup
                kendo.ui.Grid.fn.destroy.call(this);
                //Cleanup all of the draggable/dropable stuff
                el.find("*").removeData().off();
                this._cleanupAddButton();
                this._cleanupPageable();
                this._cleanupCheckBox();
            }
        },

        _error: function() {
            kendo.ui.Grid.fn._error.call(this);
            this.trigger("dfgrid/error");
        },

        _wasEnabledSet: false,

        enabled: true,

        _prepareDisableHook: function() {
            var me = this;
            var stop = function(e) {
                if (!me.enabled) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            };
            this.element[0].addEventListener("mousedown", stop, true);
            this.element[0].addEventListener("mouseup", stop, true);
            this.element[0].addEventListener("click", stop, true);
            this.element[0].addEventListener("pointerdown", stop, true);
            this.element[0].addEventListener("MSPointerDown", stop, true);
        },

        enable: function(value) {
            this.enabled = value ? true : false;
            this._wasEnabledSet = true;
            this.element.toggleClass("prevent-scroll", !this.enabled);
            this.table.attr("tabindex", this.enabled ? "0" : "-1");
            this._onEnable(this.enabled);
        },

        // navigatable (which set table tabindex) gets called inside setDataSource so we need to resync the enabled state on refresh
        _refreshEnabled: function() {
            if (this._wasEnabledSet === true) {
                this.enable(this.enabled);
            }
        },

        readonly: function(to) {
            this.options.readonly = !!to;
            this._readonlyReorderable();
            this._toggleEditableColumn();
        },

        focus: function() {
            if (this.enabled !== true) {
                return;
            }
            var before = null;
            if (this.content && this.content.length) {
                before = this.content.scrollTop();
            }
            this.table.focus();
            if (before !== null) {
                this.content.scrollTop(before);
            }
        },

        /**
         * Override
         */
        getARIAElements: function() {
            return $(this.table);
        },

        enableSelection: function(val) {
            this.element.toggleClass("hideGridSelection", !val);
        },

        /**
         * Select the first row of the grid.
         */
        selectFirst: function() {

            var rows = this.tbody.find('tr[data-uid]:first');
            if (rows.length == 1) {
                this.select(rows[0]);
            }
        },

        /**
         * Select a row by index.  If the given index is out-of-range the closes possible index is selected.
         * If no rows are available, the selection is cleared.
         * @param index
         */
        selectIndex: function(index) {
            var tr = this.tbody.find("tr[data-uid]");
            if (index >= tr.length) {
                index = tr.length - 1;
            } else if (index < 0) {
                index = 0;
            }
            if (tr.length) {
                this.select(tr[index]);
            } else {
                this.clearSelection();
            }
        },

        /**
         * Getter/setter for row value.
         *
         * When a value is set, it will only select a row until the grid is refreshed.
         * The value binding handles keep the value sticky if necessary.
         * In addition, the keepSelectionOnRefresh option can be used if the value binding is not needed. Note that this option has some very specific rules.
         * @param value
         */
        value: function(value) {
            var valueField = this.options.dataValueField;
            if (!valueField) {
                throw new Error("dataValueField must be defined to use value method");
            } else if (arguments.length) {
                var matches = this.dataSource.view().filter(function(item) {
                    return item[valueField] == value;
                });
                if (matches.length == 1) {
                    this.select("[data-uid='" + matches[0].uid + "']");
                } else {
                    this.clearSelection();
                }
            } else {
                var item = this.dataItem(this.select());
                if (item) {
                    return item[valueField];
                } else {
                    return null;
                }
            }
        },

        /**
         * Override
         * Ensure certain columns like the checkbox and spacer don't get reordered
         * @param destIndex destination index
         * @param column reorder column
         */
        reorderColumn: function(destIndex, column) {
            const staticStart = [
                this.options.checkboxField || "checkbox",
                this.options.reorderableRows || "dragColumn"
            ];
            const staticEnd = [
                "spacer"
            ];
            if([...staticStart, ...staticEnd].includes(column.field)) {
                return;
            }
            const cannotReorder = (fields, atStart = true) => {
                for(const seek of fields) {
                    const i = this.columns.findIndex(({field}) => field === seek);
                    if(i === -1) {
                        continue;
                    }
                    if(atStart && destIndex <= i || !atStart && destIndex >= i) {
                        return true;
                    }
                }
            }
            if(cannotReorder(staticStart) || cannotReorder(staticEnd, false)) {
                return;
            }
            kendo.ui.Grid.fn.reorderColumn.apply(this, arguments);
        },

        /**
         * Override
         * DFGridKeyboard uses current() to determine when to stop key propagation, need to make sure it's accurate when editing
         */
        editRow(row) {
            if(this.options.navigatable && this.options.editable && this.options.editable.mode === "inline") {
                this.current($(row).find("td:visible:first"));
            }
            return kendo.ui.Grid.fn.editRow.apply(this, arguments);
        },

        /**
         * Override
         * kendo no longer triggers change on select() as of 2022.1.412 and we rely on it https://github.com/telerik/kendo-ui-core/issues/5631
         */
        select() {
            if(arguments.length > 0) {
                kendo.ui.Grid.fn.select.apply(this, arguments);
                this.trigger("change");
            } else {
                return kendo.ui.Grid.fn.select.call(this);
            }
        },

        clearSelection() {
            kendo.ui.Grid.fn.clearSelection.call(this);
            this.trigger("change");
        }
    });

kendo.ui.plugin(DFGrid);
export default DFGrid;
